import { useEffect, useState } from 'react';
import { PUBLISHABLE_STATUSES, type StreamStatus } from '@zip2live/shared/stream-state';
import { api } from '../lib/api.ts';
import { useMe, type Me } from '../lib/me.ts';
import { navigate } from '../lib/router.ts';

/** This person's browser broadcast that still takes video, if any (OBS streams reconnect from OBS). */
export function openBroadcast(me: Me | null, skip?: ReadonlySet<string>): Me['streams'][number] | null {
  return me?.streams.find((s) => s.source !== 'obs' && PUBLISHABLE_STATUSES.includes(s.status as StreamStatus) && !skip?.has(s.id)) ?? null;
}

/** Where "back to your stream" goes: the Go Live page picks the stream up as soon as the camera is on. */
export const resumePath = (id: string) => `/go-live?resume=${encodeURIComponent(id)}`;

/**
 * A bar above the header while this person has a browser stream that is still on: they closed the
 * tab, lost their connection, or left the page to sign in. One tap takes them back to it.
 */
export function LiveReturnBanner() {
  const { me } = useMe();
  const candidate = openBroadcast(me);
  const id = candidate?.id ?? null;
  // The account list can be minutes old: ask the stream itself, and keep asking while the bar shows.
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    setStatus(null);
    if (!id) return;
    let stop = false;
    const check = () =>
      api.getStream(id).then(
        (s) => !stop && setStatus(s.status),
        () => !stop && setStatus(null),
      );
    void check();
    const t = setInterval(check, 15_000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [id]);

  if (!id || !status || !PUBLISHABLE_STATUSES.includes(status as StreamStatus)) return null;
  const [text, more] =
    status === 'RECONNECTING' ? ['Your stream dropped.', ' It ends soon unless you come back.']
    : status === 'LIVE' || status === 'CONNECTING' ? ['You’re live.', '']
    : ['Your stream link is ready.', ''];
  return (
    <div className="bg-race text-white" role="status">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2 sm:px-8">
        <p className="flex min-w-0 items-center gap-2 text-sm font-bold">
          <span className="live-dot h-2 w-2 shrink-0 rounded-full bg-white" aria-hidden />
          <span className="min-w-0">
            {text}
            <span className="hidden sm:inline">{more}</span>
          </span>
        </p>
        <a
          href={resumePath(id)}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigate(resumePath(id));
          }}
          aria-label="Back to your stream"
          className="shrink-0 rounded-full bg-white px-3.5 py-1.5 text-sm font-extrabold text-race hover:bg-mist"
        >
          Back to <span className="hidden sm:inline">your </span>stream
        </a>
      </div>
    </div>
  );
}
